import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FiArrowLeft, FiSave, FiSearch } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { FormHandles } from '@unform/core';
import { useDispatch } from 'react-redux';

import TextArea from 'components/TextArea';
import Button from 'components/Button';
import Spinner from 'components/Spinner';
import Input from 'components/Input';
import Card from 'components/Card';
import Select from 'components/Select';
import Radio from 'components/Radio';

import { Programa } from 'pages/@types';
import api from 'services/api';
import AppError from 'utils/AppError';
import getValidationErrors from 'services/getValidationErros';
import { show } from 'store/modules/dialog/actions';
import { StoreContainer as Container, Form, FormGroups } from './styles';
import SelectCreatable from 'components/SelectCreatable';
import Tooltip from 'components/Tooltip';
import { Categoria } from 'utils/variables';

// configuração do validator
const schema = Yup.object().shape({
  titulo: Yup.string()
    .required('Campo Obrigatório')
    .max(45, 'Titulo excedeu o limite de 45 caracteres'),
  versao: Yup.string()
    .nullable()
    .required('Campo Obrigatório'),
  duracao: Yup.string().required('Campo Obrigatório'),
  programa: Yup.string()
    .required('Campo Obrigatório')
    .nullable(),
});

export default function OptativoLocalStore() {
  // react-router-dom
  const navigate = useNavigate();
  /**
   * USE STATE
   */
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [loadingProgramas, setLoadingProgramas] = useState(false);
  const [programas, setProgramas] = useState<Programa[]>([]);
  /**
   * REDUX
   */
  const dispatch = useDispatch();
  /**
   * USE REF
   */
  const formRef = useRef<FormHandles>(null);

  /**
   * FUNÇÕES
   */
  const handleSuccessMessage = useCallback(
    ({ contentHTML, redirectTo }) => {
      const option = {
        type: 'success',
        confirmButtonText: '',
        contentHTML,
        timer: 3000,
        cancelButtonText: 'NÃO',
        redirectTo,
        showCancelButton: false,
        showConfirmButton: false,
        showCloseButton: true,
      };

      dispatch(show(option));
    },
    [dispatch],
  );

  const handleSubmit = useCallback(
    async data => {
      try {
        if (formRef.current) {
          setLoadingSubmit(true);
          formRef.current.setErrors({});
          await schema.validate(data, { abortEarly: false });

          const response = await api.post('/optativolocal', {
            ...data,
            titulo: data.titulo.toUpperCase().trim(),
            versao: data.versao.toUpperCase(),
          });

          if (response && response.data)
            handleSuccessMessage({
              contentHTML: `<h3>Optativo Local criado!</h3> <p>Você será redirecionado para a lista de Optativos Locais em 3 segundos</p>`,
              redirectTo: `/optativoslocais`,
            });
          else toast.error('Falha ao salvar registro de Optativo Local');
        }
      } catch (err) {
        console.log(err);
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          if (formRef.current) formRef.current.setErrors(errors);
          toast.error(err.message);
        } else if (err instanceof Error) {
          toast.error(err.message);
        } else {
          const appError = err as AppError;
          toast.error(appError.message);
        }
      }
      setLoadingSubmit(false);
    },
    [handleSuccessMessage],
  );

  useEffect(() => {
    async function loadProgramas() {
      setLoadingProgramas(true);
      try {
        const response = await api.get('/programas');
        if (response && response.data) {
          setProgramas(
            response.data
              .filter(p => p.categoria === Categoria.OPTATIVO)
              .map(p => ({
                value: p.id,
                label: p.mnemonico,
              })),
          );
        } else toast.error('Falha de comunicação com o sistema');
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message);
        } else {
          toast.error('Falha ao carregar programas');
          console.log(error);
        }
      }
      setLoadingProgramas(false);
    }
    loadProgramas();
  }, []);

  return (
    <Container>
      <Card>
        <Form ref={formRef} onSubmit={handleSubmit}>
          <div>
            <header>
              <h4>CADASTRAR OPTATIVO LOCAL</h4>
            </header>
            <div>
              <div>
                <section>
                  <h5>
                    Título: * (O título deve ser exatamente igual ao nome do
                    arquivo)
                  </h5>
                </section>
                <Input name="titulo" placeholder="Título do Arquivo *" />
              </div>
              <div>
                <FormGroups $columns="3fr 1fr 2fr">
                  <div>
                    <section>
                      <h5>Programa: *</h5>
                    </section>
                    <SelectCreatable
                      max={100}
                      isClearable
                      menuPlacement="auto"
                      isMulti={false}
                      errorField=""
                      name="programa"
                      placeholder="Programa"
                      options={programas}
                      searchable
                      createOptionPosition="first"
                      fill={false}
                    />
                  </div>
                  <div>
                    <section>
                      <h5>Duração: *</h5>
                    </section>
                    <Input
                      name="duracao"
                      placeholder="Duração *"
                      type="time"
                      step="1"
                    />
                  </div>
                  <div>
                    <section>
                      <h5>Versão: *</h5>
                    </section>
                    <Radio
                      name="versao"
                      options={[
                        { id: 'unico', label: 'ÚNICO' },
                        { id: 'maior', label: 'MAIOR' },
                        { id: 'menor', label: 'MENOR' },
                      ]}
                      disabled={loadingSubmit}
                    />
                  </div>
                </FormGroups>
              </div>
              <div>
                <section>
                  <h5>Observações:</h5>
                </section>
                <TextArea
                  name="obseditor"
                  height="100"
                  placeholder="Observações"
                />
              </div>
            </div>
          </div>
          <div className="ButtonGroup">
            <Button
              color="primary"
              type="button"
              onClick={e => {
                e.preventDefault();
                navigate(-1);
              }}
            >
              <FiArrowLeft />
              VOLTAR
            </Button>
            <Button className="submitButton" type="submit" color="primary">
              <FiSave />
              {!loadingSubmit ? 'SALVAR' : <Spinner type="ThreeDots" />}
            </Button>
          </div>
        </Form>
      </Card>
    </Container>
  );
}
